import { Chip, createStyles, Grid, makeStyles, Theme, useMediaQuery } from "@material-ui/core";
import Autocomplete from "components/Autocomplete";
import { GeoAutoCompletion } from "components/GeoAutoCompletion";
import { TextField } from "components/TextField";
import { theme } from "config/theme";
import useText from "constants/text";
import { checkReccurenceIntervalDate, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { EProductType, EReccurenceType, ReccurenceOptionsInput } from "generated/graphql";
import React from "react";
import { formatDate as DateFormat } from "utils";
import { ErrorStep } from "../shared/MainStyles";
import Reccurence from "../shared/Reccurence";
import { formatDate, isFieldWithValue, isSlotIntervallCorrect, isSunday } from "../shared/utils";

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    addressGeo: {
      [theme.breakpoints.down("xs")]: {
        "& .MuiFormControl-fullWidth": {
          width: "104%",
        },
      },
    },
    selectType: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        marginTop: -5,
      },
    },
    dateInput: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        marginTop: -30,
        marginBottom: -14,
      },
    },
    slotInput: {
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        marginBottom: -14,
      },
    },

    root: {
      justifyContent: "center",
      padding: "3rem 3rem 0rem 1.5rem",
      "&.MuiGrid-spacing-xs-10 > .MuiGrid-item": {
        padding: "25px",
      },
      [theme.breakpoints.down("xs")]: {
        padding: "2rem 1rem 2.5rem 0.5rem",
        justifyContent: "center",
        width: "118%",
        marginLeft: -36,
      },
    },
    productTypeChip: {
      backgroundColor: "#399ebf",
      color: "white",
    },
  })
);

export default function Rescheduling() {
  const { requestQuote, setRequestQuote } = React.useContext(NewRequestQuoteContext);
  const classes = useStyles();
  const { text } = useText();
  const smallScreen = useMediaQuery(theme.breakpoints.down("xs"));

  React.useEffect(() => {
    if (!isFieldWithValue(requestQuote?.startDate)) {
      setRequestQuote({ ...requestQuote, startDate: formatDate(0) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productsTypeOptions = [
    {
      value: EProductType.Couriers,
      description: text.requestQuote.product_type_courrier,
    },
    {
      value: EProductType.Fragile,
      description: text.requestQuote.product_type_fragile,
    },
    {
      value: EProductType.Food,
      description: text.requestQuote.product_type_food,
    },
    {
      value: EProductType.FreshProducts,
      description: text.requestQuote.product_type_fresh_products,
    },
    {
      value: EProductType.Other,
      description: text.requestQuote.product_type_other,
    },
  ];
  const [filterOptions, setFilterOptions] = React.useState(productsTypeOptions);

  React.useEffect(() => {
    if (requestQuote?.productType) {
      setFilterOptions(
        productsTypeOptions.filter(
          (x) => requestQuote?.productType.findIndex((e: any) => x.value === e?.value || x.value === e) === -1
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestQuote?.productType]);

  const onChangeTime = (event: React.ChangeEvent<HTMLInputElement>) => {
    const timeHour = event.target.value.split(":");
    const time = parseInt(timeHour[0], 10);
    if (!isNaN(time)) setRequestQuote({ ...requestQuote, [event.target.name]: `${timeHour[0]}:${timeHour[1]}` });
  };

  const onChangeDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRequestQuote({ ...requestQuote, [event.target.name]: event.target.value });
  };

  const onReccurenceChange = (reccurenceType: EReccurenceType, options?: ReccurenceOptionsInput) => {
    setRequestQuote({ ...requestQuote, reccurenceType, reccurenceOptions: options });
  };

  const handleChange = (newArray: any) => {
    setRequestQuote({
      ...requestQuote,
      productType: newArray.map((productType: any) => productType?.value || productType),
    });
  };

  const getOptionValueDescription = (option: any) =>
    productsTypeOptions?.find((item) => option?.value === item.value || option === item.value)?.description || "";

  return (
    <NewRequestQuoteContext.Consumer>
      {({ requestQuote, fieldError }) => (
        <Grid container spacing={5} className={classes.root}>
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              multiple
              id="fixed-tags-demo"
              label={text.requestQuote.select_product_type}
              value={requestQuote?.productType}
              onChange={(event: any, newValue: any) => {
                handleChange(newValue);
              }}
              options={filterOptions}
              getOptionLabel={(option: any) => option.description}
              renderTags={(tagValue: any, getTagProps: any) =>
                tagValue.map((option: any, index: any) => (
                  <Chip
                    label={getOptionValueDescription(option)}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...getTagProps({ index })}
                    className={classes.productTypeChip}
                  />
                ))
              }
              error={fieldError && !requestQuote?.productType?.length}
              helperText={fieldError && !requestQuote?.productType?.length ? text.all.this_field_is_required : ""}
            />
          </Grid>

          <Grid item xs={12} className={classes.addressGeo}>
            <GeoAutoCompletion
              address={requestQuote?.address}
              onChange={(values) => setRequestQuote({ ...requestQuote, address: values })}
              error={fieldError && !isFieldWithValue(requestQuote?.address?.place_id)}
              helperText={
                fieldError && !isFieldWithValue(requestQuote?.address?.place_id) ? text.all.this_field_is_required : ""
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.dateInput}>
            <TextField
              type="date"
              fullWidth
              label={text.all.start_date}
              name="startDate"
              id="date"
              value={DateFormat(requestQuote?.startDate, "YYYY-MM-dd") || formatDate(0)}
              onChange={onChangeDate}
              inputProps={{ min: formatDate(0) }}
              error={
                fieldError && (!isFieldWithValue(requestQuote?.startDate) || !checkReccurenceIntervalDate(requestQuote))
              }
              helperText={
                fieldError
                  ? !isFieldWithValue(requestQuote?.startDate)
                    ? text.all.this_field_is_required
                    : !checkReccurenceIntervalDate(requestQuote)
                    ? text.all.intervall_date_error
                    : ""
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.slotInput}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  id="time_from"
                  label={text.all.slot_start}
                  type="time"
                  name="slotStart"
                  value={requestQuote?.slotStart || ""}
                  onChange={onChangeTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                  inputProps={{
                    step: 3600,
                  }}
                  error={
                    fieldError &&
                    (!isFieldWithValue(requestQuote?.slotStart) ||
                      !isSlotIntervallCorrect(requestQuote?.slotStart, requestQuote?.slotEnd))
                  }
                  helperText={
                    fieldError
                      ? !isFieldWithValue(requestQuote?.slotStart)
                        ? text.all.this_field_is_required
                        : !isSlotIntervallCorrect(requestQuote?.slotStart, requestQuote?.slotEnd)
                        ? text.all.intervall_slot_error
                        : ""
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="time_to"
                  label={text.all.slot_end}
                  type="time"
                  name="slotEnd"
                  value={requestQuote?.slotEnd || ""}
                  onChange={onChangeTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  fullWidth
                  inputProps={{
                    step: 3600,
                  }}
                  error={
                    fieldError &&
                    (!isFieldWithValue(requestQuote?.slotEnd) ||
                      !isSlotIntervallCorrect(requestQuote?.slotStart, requestQuote?.slotEnd))
                  }
                  helperText={
                    fieldError
                      ? !isFieldWithValue(requestQuote?.slotEnd)
                        ? text.all.this_field_is_required
                        : !isSlotIntervallCorrect(requestQuote?.slotStart, requestQuote?.slotEnd)
                        ? text.all.intervall_slot_error
                        : ""
                      : ""
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Reccurence
            reccurence={requestQuote}
            onReccurenceChange={onReccurenceChange}
            onchangeEndRequestDate={onChangeDate}
            endRequestDate={requestQuote?.endDate}
            startRequestDate={requestQuote?.startDate}
            error={fieldError && !isFieldWithValue(requestQuote?.reccurenceType)}
            dateError={fieldError && !checkReccurenceIntervalDate(requestQuote)}
            forSamaPapser
            padding="40px !important"
          />
          {isSunday(requestQuote?.startDate) && <ErrorStep>{text.all.sunday_error}</ErrorStep>}
        </Grid>
      )}
    </NewRequestQuoteContext.Consumer>
  );
}
